import { FC, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { LoadError } from 'components/LoadError';
import { TabAccordion } from 'components/Forms/TabAccordion';
import { has_permission } from 'config/permissions';
import { useAppDispatch } from 'helpers/hooks';
import { useAccordionTab } from 'helpers/useAccordionTab';
import { useAdminAuth } from 'hooks/useAdminAuth';
import { useGetShoppingCartQuery } from 'services/shoppingCartApi';
import { getRecipients } from 'store/recipients/actions';
import { RecipientsRequestType } from 'store/recipients/types';
import { AdminShoppingCartResponse } from 'store/shoppingCart/types';
import { ShoppingCartAccordion } from './ShoppingCartAccordion';
import { getActivateInitialValues, getEditInitialValues } from './formValues';
import { useShoppingCartSubmit } from './useShoppingCartSubmit';
import { ShoppingCartFormValues } from './types';
import {
  CART_LOAD_ERROR,
  SHOPPING_CART_CHECKS_TAB,
  SHOPPING_CART_PAGE_TYPE,
  SHOPPING_CART_SECTIONS,
} from './constants';

export const ShoppingCartDetail: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { clientId, activeRole, hasClient } = useAdminAuth();
  const canViewCart = has_permission('SHOPPING_CART_DETAIL_ROUTE', 'R', activeRole);
  const [tabStatus, setTabStatus] = useState<string[]>(['in-progress', '', '']);
  const updateStatus = (item: number, status: string) => {
    setTabStatus(prev => prev.map((s, index) => (index === item ? status : s)));
  };
  const { handleClickAccordion, tabIndexVisible } = useAccordionTab({
    updateStatus,
    tabStatus,
    pageType: SHOPPING_CART_PAGE_TYPE,
    checksTab: SHOPPING_CART_CHECKS_TAB,
  });

  const {
    currentData: fetchedCartData,
    isError,
    refetch,
  } = useGetShoppingCartQuery({ clientId: clientId ?? 0 }, { skip: !hasClient || !canViewCart });

  const [snapshot, setSnapshot] = useState<{
    clientId: number | null;
    data: AdminShoppingCartResponse | undefined;
  }>({ clientId, data: undefined });
  if (snapshot.clientId !== clientId || (!snapshot.data && fetchedCartData)) {
    setSnapshot({ clientId, data: fetchedCartData });
  }
  const cartData = snapshot.data;

  const cart = cartData?.cart;
  const isEditing = !!cart;

  // Memoized so re-renders don't hand the form a new object and reinitialize it mid-edit.
  const initialValues = useMemo(
    () =>
      cartData?.cart
        ? getEditInitialValues(cartData.cart)
        : getActivateInitialValues(cartData?.suggested_query_string),
    [cartData]
  );

  useEffect(() => {
    if (canViewCart && !hasClient) {
      navigate('/shopping_cart', { replace: true });
    }
  }, [canViewCart, hasClient, navigate]);

  useEffect(() => {
    if (canViewCart && hasClient) {
      dispatch(
        getRecipients({ payment_processor: 'all', request_type: RecipientsRequestType.admin })
      );
    }
  }, [canViewCart, hasClient, dispatch]);

  const handleSubmit = useShoppingCartSubmit(cart);

  if (!activeRole) return null;

  if (!canViewCart) {
    return (
      <div className="content-full-width">
        <h1 className="title-permission">
          Sorry, you don&apos;t have permission to see this screen.
        </h1>
      </div>
    );
  }

  if (isError) return <LoadError message={CART_LOAD_ERROR} onRetry={refetch} />;

  if (!hasClient || !cartData) return null;

  return (
    <div className="content-full-width">
      <Form<ShoppingCartFormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit: submitForm, form, submitting }) => (
          <form onSubmit={submitForm} className="detail-form">
            <DetailHeader
              saveForm={submitForm}
              type="Shopping Cart"
              isNew={!isEditing}
              value={isEditing ? 'Save' : 'Activate'}
              backTo="/shopping_cart"
              backLabel="Back to Shopping Cart"
            />
            {SHOPPING_CART_SECTIONS.map((type, index) => (
              <div key={type}>
                <TabAccordion
                  index={index}
                  itemVisible={tabIndexVisible}
                  type={type}
                  tabStatus={tabStatus}
                  handleClickAccordion={() => handleClickAccordion(index, form, 'tab')}
                />
                <ShoppingCartAccordion
                  index={index}
                  itemVisible={tabIndexVisible}
                  cartRecipients={cart?.recipients}
                  isEditing={isEditing}
                  isSubmitting={submitting}
                  onNext={() => handleClickAccordion(index + 1, form, 'next')}
                />
              </div>
            ))}
          </form>
        )}
      />
    </div>
  );
};
